import { inject, Injectable } from '@angular/core';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { LoggerService } from '@pf/shared-services';
import { RoutesForReceiving } from './receiving.routes';
import {
    PurchaseOrderHeaderDto,
    PurchaseOrderHeaderFacade,
} from '@pf/platform-orders/domain';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    delay,
    filter,
    first,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { uniq } from 'lodash-es';
import { PoLineSummary } from '../models/po-line-summary.model';
import { PoLookupService } from '../services/po-lookup.service';
import {
    AsnHeaderDto,
    AsnHeaderFacade,
    ContainerDto,
    ContainerFacade,
    ExceptionDto,
    ExceptionFacade,
    ReceiptHeaderFacade,
    ReceiptLineFacade,
    ReceiptLineDto,
    RequisitionDto,
    RequisitionFacade,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import {
    AsnStatus,
    PurchaseOrderStatus,
    WarehouseEquipmentCategory,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';
import {
    LineConfirmSelection,
    ReceiptLineMapping,
    ReceivingContext,
} from '../models/receiving-context.model';
import { RmaContext } from '../models/rma-context.model';
import { RmaFacade } from './rma.facade';
import { ReceivingTask } from '../models/receiving-task.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PhotoCaptureFile } from '../models/photo-capture.file';
import { ReceivingReceiptService } from '../services/receiving-receipt.service';
import {
    ReceiptLineShipmentKey,
    ReceivingDocumentationService,
} from '../services/receiving-documentation.service';
import { HandlingUnitAssignmentService } from '../services/handling-unit-assignment.service';
import { ReceivingShipment } from '../models/receiving-shipment.model';
import { containerHasAssignedBundles } from '../utils/container-inventory.util';
import { HandlingUnitsContext } from '../models/handling-units-context.model';

/** Possible validation errors when looking up a purchase order for receiving. */
export enum ReceivingValidationError {
    NotFound = 1,
    WrongWarehouse = 2,
    Received = 3,
    RequisitionNotFound = 4,
    InvalidPurchaseOrder = 5,
}

/** Result of a purchase-order lookup including validation status. */
export interface ReceivingLookupResult {
    purchaseOrder: PurchaseOrderHeaderDto | null;
    requisition: RequisitionDto | null;
    valid: boolean;
    error?: ReceivingValidationError;
}

/** Result of validating a scanned cart bin before receiving put-away assignment. */
export type PutawayCartBinValidation = {
    ok: boolean;
    container?: ContainerDto;
    message?: string;
};

/**
 * Central facade for the receiving workflow.
 *
 * Coordinates purchase-order lookup, line selection, receipt creation,
 * shipment documentation, and putaway across the CPW receiving UI.
 */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ReceivingFacade {
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly handlingUnitAssignmentService = inject(
        HandlingUnitAssignmentService
    );
    private readonly logger = inject(LoggerService);
    private readonly routes = inject(RoutesForReceiving);
    private readonly exceptionFacade = inject(ExceptionFacade);
    private readonly purchaseOrderFacade = inject(PurchaseOrderHeaderFacade);
    private readonly poLookupService = inject(PoLookupService);
    private readonly requisitionFacade = inject(RequisitionFacade);
    private readonly receiptService = inject(ReceivingReceiptService);
    private readonly documentationService = inject(
        ReceivingDocumentationService
    );
    private readonly receiptHeaderFacade = inject(ReceiptHeaderFacade);
    private readonly receiptLineFacade = inject(ReceiptLineFacade);
    private readonly asnHeaderFacade = inject(AsnHeaderFacade);
    private readonly containerFacade = inject(ContainerFacade);
    private readonly rmaFacade = inject(RmaFacade);

    /** Emits `true` while the persisted user-position session is being restored. */
    loadingUserSession = new BehaviorSubject(true);

    /** Snapshot of the current active receiving task (synchronous). */
    activeReceivingTask = () =>
        this.taskStateService.getActiveTask<ReceivingTask>();

    /** Emits the current active receiving task whenever it changes. */
    activeReceivingTask$ = this.taskStateService.activeTask$.pipe(
        map(task => task as ReceivingTask),
        untilDestroyed(this)
    );

    /** Emits the active purchase order or `null`. */
    purchaseOrder$ = this.purchaseOrderFacade.active$.pipe(
        untilDestroyed(this)
    );

    /** Shared state combining loading flag and PO-line data to avoid shareReplay race conditions. */
    private readonly _poLineState$ = this.purchaseOrder$.pipe(
        switchMap(po => {
            if (!po) {
                return of({ loading: false, lines: [] as PoLineSummary[] });
            }
            return this.poLookupService.getPoLines$(po).pipe(
                map(lines => ({ loading: false, lines })),
                startWith({ loading: true, lines: [] as PoLineSummary[] })
            );
        }),
        untilDestroyed(this),
        shareReplay(1)
    );

    /** Emits enriched PO-line summaries for the active purchase order. */
    poLineSummaries$ = this._poLineState$.pipe(
        filter(state => !state.loading),
        map(state => state.lines)
    );

    /** Emits only the PO-line summaries whose IDs are selected in the task context. */
    selectedLines$ = combineLatest([
        this.poLineSummaries$,
        this.activeReceivingTask$,
    ]).pipe(
        map(([lines, task]) => {
            const selectedIds = new Set(task?.context?.selectedLineIds ?? []);
            return lines.filter(l => selectedIds.has(l.id));
        }),
        untilDestroyed(this)
    );

    /** Emits `true` while PO-line summaries are loading, `false` once available. */
    loadingPoLines$ = this._poLineState$.pipe(
        map(state => state.loading),
        untilDestroyed(this),
        shareReplay(1)
    );

    /** Emits `true` when every PO line has status 'complete', `false` otherwise. */
    allPoLinesComplete$ = this.poLineSummaries$.pipe(
        map(
            lines =>
                lines.length > 0 && lines.every(l => l.status === 'complete')
        ),
        untilDestroyed(this),
        shareReplay(1)
    );

    /**
     * Enriches a purchase order's lines with receipt-derived status
     * (Not Started / Partial / Complete / Short / Overage). Used by the
     * Receiving "Purchase Orders" tab to show real per-line status instead of
     * the order-level status. One-shot; callers load this lazily per PO.
     */
    getPoLineSummaries$(
        po: PurchaseOrderHeaderDto
    ): Observable<PoLineSummary[]> {
        return this.poLookupService.getPoLines$(po);
    }

    /**
     * Emits the list of {@link ReceivingShipment}s that are ready to be
     * counted and put away.
     *
     * Pipeline:
     * 1. Extract receipt-line mappings from the active task context.
     * 2. Load any existing exceptions for the mapped receipt lines.
     * 3. Combine with selected PO lines to produce `ReceivingShipment[]`.
     */
    shipmentsToReceive$ = this.activeReceivingTask$.pipe(
        map(task => task?.context?.receiptLineMappings ?? []),
        filter(mappings => mappings.length > 0),
        switchMap(mappings =>
            this.loadExceptionsForMappings$(mappings).pipe(
                map(exceptions => ({
                    exceptions,
                    keys: uniq(
                        mappings.map(m =>
                            ReceiptLineShipmentKey(m.poLineId, m.asnHeaderId)
                        )
                    ),
                    mappings,
                }))
            )
        ),
        switchMap(({ exceptions, keys, mappings }) =>
            this.buildReceivingShipments$(keys, exceptions, mappings)
        ),
        untilDestroyed(this),
        shareReplay(1)
    );

    constructor() {
        this.loadUserSession();
    }

    /**
     * Restores a previously persisted receiving session from the user-position context.
     *
     * Waits until the user position is loaded, extracts the stored PO / requisition IDs,
     * fetches both entities, and re-activates the receiving task.
     */
    loadUserSession() {
        this.userPositionFacade.loadingActive$
            .pipe(
                filter(loading => !loading),
                first(),
                map(() => this.userPositionFacade.active),
                map(
                    userPosition =>
                        userPosition!.context as ReceivingContext & {
                            rma?: RmaContext;
                        }
                ),
                switchMap(context => this.restoreSessionFromContext$(context)),
                delay(1000),
                tap(() => {
                    this.loadingUserSession.next(false);
                }),
                catchError(err => {
                    this.logger.error(
                        'Failed to restore receiving session from user position',
                        err
                    );
                    this.loadingUserSession.next(false);
                    return of(null);
                })
            )
            .subscribe();
    }

    /** Navigates to the expected-shipments dashboard. */
    expectedShipments() {
        this.routes.dashboard().navigate();
    }

    /** Navigates to the PO lookup / process screen. */
    poLookup() {
        this.routes.process().navigate();
    }

    /** Navigates to the process screen pre-filled with the given PO order number. */
    poLookupForOrder(orderNumber: string) {
        this.routes.processForOrder(orderNumber).navigate();
    }

    /**
     * Resets the active receiving task and clears all context.
     * @param clearHandlingUnits When `true`, releases all assigned handling units before clearing.
     */
    clearActiveReceivingTask(clearHandlingUnits = false) {
        this.purchaseOrderFacade.resetActive();
        this.requisitionFacade.resetActive();

        const activeTaskId = this.taskStateService.getActiveTask()?.id;

        const clearContext = () => {
            if (!activeTaskId) {
                this.logger.warn(
                    'clearActiveReceivingTask called with no active task'
                );
                return;
            }
            this.taskStateService
                .updateTaskContext$<ReceivingContext>(activeTaskId, {
                    purchaseOrderId: null,
                    requisitionId: null,
                    selectedLineIds: [],
                    receiptHeaderIds: [],
                    receiptLineMappings: [],
                    ...(clearHandlingUnits
                        ? ({
                              assignedHandlingUnitIds: [],
                              latestHandlingUnitId: undefined,
                          } as HandlingUnitsContext)
                        : {}),
                })
                .pipe(tap(() => this.taskStateService.clearActiveTask()))
                .subscribe();
        };

        if (clearHandlingUnits) {
            this.handlingUnitAssignmentService
                .releaseAllHandlingUnits$()
                .subscribe(() => clearContext());
        } else {
            clearContext();
        }
    }

    /**
     * Creates and activates a new receiving task for the given PO and requisition.
     *
     * Initializes a fresh {@link ReceivingContext}, registers the task with the
     * task-state service, and persists the context when it differs from the
     * current user-position.
     */
    setActiveReceivingTask$(
        purchaseOrder: PurchaseOrderHeaderDto,
        requisition: RequisitionDto
    ) {
        const context: ReceivingContext = {
            purchaseOrderId: purchaseOrder.id,
            requisitionId: requisition.id,
            selectedLineIds: [],
            receiptHeaderIds: [],
            receiptLineMappings: [],
            assignedHandlingUnitIds: [],
            latestHandlingUnitId: undefined,
        };
        const task: ReceivingTask = {
            claimedAt: new Date(),
            context,
            details: '',
            group: '',
            id: `${purchaseOrder.id}_${requisition.id}`,
            priority: false,
            purchaseOrder,
            readonly: false,
            requisition,
            taskIconType: 'receiving',
            time: new Date(),
            title: '',
            warehouseId: requisition.warehouse.id as string,
        };
        this.taskStateService.clearTasks();
        this.taskStateService.setTasks([task]);
        this.taskStateService.setActiveTask(task.id);
        this.purchaseOrderFacade.setActive(purchaseOrder.id);
        this.requisitionFacade.setActive(requisition.id);
        this.taskStateService.claimActiveTask();

        const userPositionContext = this.userPositionFacade.active!
            .context as ReceivingContext;

        if (
            userPositionContext.requisitionId !== context.requisitionId ||
            userPositionContext.purchaseOrderId !== context.purchaseOrderId
        ) {
            this.taskStateService
                .updateTaskContext$<ReceivingContext>(task.id, context)
                .subscribe();
        }
    }

    /**
     * Looks up a purchase order by PO number or ASN tracking number and
     * validates it against the current warehouse.
     *
     * @returns An observable emitting the lookup result with validation status.
     */
    lookupAndValidatePurchaseOrder$(
        poOrAsnTrackingNumber: string
    ): Observable<ReceivingLookupResult> {
        return this.poLookupService
            .lookupByPoOrAsnTrackingNumber$(poOrAsnTrackingNumber)
            .pipe(
                switchMap(purchaseOrder =>
                    this.validatePurchaseOrder$(purchaseOrder)
                )
            );
    }

    /** Persists the user's PO-line selection to the task context. */
    setSelectedLineIds(selectedLineIds: string[]) {
        const activeTask = this.taskStateService.getActiveTask<ReceivingTask>();
        if (!activeTask) {
            this.logger.warn(
                'setSelectedLineIds called with no active receiving task'
            );
            return;
        }
        this.taskStateService
            .updateTaskContext$<ReceivingContext>(activeTask.id, {
                ...activeTask.context,
                selectedLineIds,
            })
            .subscribe();
    }

    /**
     * Confirms the selected PO lines by creating receipt headers / lines and
     * persisting the resulting mappings to the task context.
     *
     * @returns An observable emitting the created {@link ReceiptLineMapping}s.
     */
    confirmLines$(
        selections: LineConfirmSelection[],
        lines: PoLineSummary[]
    ): Observable<ReceiptLineMapping[]> {
        const activeTask = this.activeReceivingTask();
        if (!activeTask) {
            this.logger.error(
                'confirmLines$ called with no active receiving task'
            );
            return of([]);
        }

        const selectedLineIds = selections.map(s => s.lineId);

        return this.receiptService
            .confirmLines$(
                selections,
                lines,
                activeTask.purchaseOrder,
                activeTask.warehouseId
            )
            .pipe(
                switchMap(receiptLineMappings => {
                    const receiptHeaderIds = uniq(
                        receiptLineMappings.map(m => m.receiptHeaderId)
                    );
                    return this.taskStateService
                        .updateTaskContext$<ReceivingContext>(activeTask.id, {
                            ...activeTask.context,
                            selectedLineIds,
                            receiptHeaderIds,
                            receiptLineMappings,
                        })
                        .pipe(map(() => receiptLineMappings));
                }),
                catchError(err => {
                    this.logger.error(
                        `Failed to confirm lines for PO ${activeTask.purchaseOrder?.id}`,
                        err
                    );
                    return of([]);
                })
            );
    }

    /**
     * Uploads shipping documentation images for the specified shipment keys.
     * @returns `true` when all uploads complete successfully.
     */
    uploadShipmentDocumentation$(
        imagesByShipmentKey: Map<string, File[]>
    ): Observable<boolean> {
        return this.documentationService.uploadShipmentDocumentation$(
            imagesByShipmentKey,
            this.getActiveReceiptLineMappings()
        );
    }

    /**
     * Loads previously uploaded shipping documentation images grouped by shipment key.
     */
    loadShipmentDocumentation$(): Observable<Map<string, PhotoCaptureFile[]>> {
        return this.documentationService.loadShipmentDocumentation$(
            this.getActiveReceiptLineMappings()
        );
    }

    /**
     * Emits the shipment keys that currently have documentation attached.
     */
    getDocumentedShipmentKeys$(): Observable<string[]> {
        return this.documentationService.getDocumentedShipmentKeys$(
            this.getActiveReceiptLineMappings()
        );
    }

    /** Updates the actual counted quantity on a receipt line. */
    updateReceiptLineActualQuantity$(
        receiptLineId: string,
        receiptHeaderId: string,
        actualQuantity: number
    ) {
        return this.receiptService.updateReceiptLineActualQuantity$(
            receiptLineId,
            receiptHeaderId,
            actualQuantity
        );
    }

    /** Updates the status of an ASN line (e.g. InTransit → Delivered). */
    updateAsnLineStatus$(
        asnLineId: string,
        asnHeaderId: string,
        status: AsnStatus
    ) {
        return this.receiptService.updateAsnLineStatus$(
            asnLineId,
            asnHeaderId,
            status
        );
    }

    /** Marks receipt lines as received into the specified container. */
    receiveLines$(
        receiptHeaderId: string,
        receiptLineIds: string[],
        receivingContainerId: string
    ) {
        return this.receiptHeaderFacade
            .receiveLines$(
                receiptHeaderId,
                receiptLineIds,
                receivingContainerId
            )
            .pipe(
                switchMap(response => {
                    return this.handlingUnitAssignmentService
                        .refreshAssignedBundles$()
                        .pipe(map(() => response));
                })
            );
    }

    clearShipmentDocumentationCache(): void {
        this.documentationService.clearShipmentDocumentationCache();
    }

    /**
     * Loads a cart bin by id and checks it is empty of inventory bundles
     * before put-away assignment.
     */
    validateCartBinForPutaway$(
        containerId: string
    ): Observable<PutawayCartBinValidation> {
        return this.containerFacade.getById$(containerId).pipe(
            first(),
            map((full): PutawayCartBinValidation => {
                if (!full?.id) {
                    return { ok: false, message: 'Container not found.' };
                }
                if (
                    full.equipment?.equipmentType?.category ===
                        WarehouseEquipmentCategory.Cart &&
                    containerHasAssignedBundles(full)
                ) {
                    return {
                        ok: false,
                        message:
                            'This bin has already been assigned. Please scan a valid bin.',
                    };
                }
                return { ok: true, container: full };
            }),
            catchError(() =>
                of<PutawayCartBinValidation>({
                    ok: false,
                    message: 'Container not found.',
                })
            )
        );
    }

    /** Marks an ASN header as Delivered once all its lines have been put away. */
    markAsnHeaderComplete$(asnHeaderId: string): Observable<AsnHeaderDto> {
        return this.asnHeaderFacade.getById$(asnHeaderId).pipe(
            switchMap(existing => {
                const dto = new AsnHeaderDto({
                    ...(existing as Partial<AsnHeaderDto>),
                    status: AsnStatus.Delivered,
                });
                return this.asnHeaderFacade.save$(dto);
            })
        );
    }

    /**
     * Creates a receipt header and line for the given PO/requisition, then
     * receives it directly into the specified non-compliance slot.
     *
     * Used when an exception is created during PO lookup (before a task is
     * claimed), so there is no active task context or existing receipt.
     */
    createReceiptAndReceiveToSlot$(
        purchaseOrder: PurchaseOrderHeaderDto,
        requisition: RequisitionDto,
        receivingContainerId: string
    ): Observable<ReceiptLineDto[]> {
        const warehouseId = this.warehouseFacade.active!.id;
        const requisitionLine = requisition.requisitionLines?.[0];
        const quantity = requisitionLine?.quantity ?? 0;

        return this.receiptService
            .createReceiptHeaderAndLine$(
                purchaseOrder,
                warehouseId,
                requisitionLine,
                quantity
            )
            .pipe(
                switchMap(({ headerId, lineId }) =>
                    this.receiptHeaderFacade.receiveLines$(
                        headerId,
                        [lineId],
                        receivingContainerId
                    )
                ),
                catchError(err => {
                    this.logger.error(
                        `Failed to create receipt and receive to slot for PO ${purchaseOrder.id}`,
                        err
                    );
                    throw err;
                })
            );
    }

    /** Navigates back to the receiving dashboard. */
    navigateToDashboard() {
        this.routes.dashboard().navigate();
    }

    // ─── Private helpers ─────────────────────────────────────────────

    /** Shorthand to get receipt-line mappings from the active task context. */
    private getActiveReceiptLineMappings(): ReceiptLineMapping[] {
        return this.activeReceivingTask()?.context.receiptLineMappings ?? [];
    }

    /**
     * Restores the receiving session from a persisted {@link ReceivingContext}.
     * @returns Observable that completes after the task is activated, or `null` if missing IDs.
     */
    private restoreSessionFromContext$(
        context: ReceivingContext & { rma?: RmaContext }
    ): Observable<unknown> {
        const contextAsAny = context as unknown as Record<string, unknown>;
        const rmaContext: RmaContext | null =
            contextAsAny['captureData'] ||
            contextAsAny['notFoundData'] ||
            contextAsAny['quarantineDone']
                ? (context as unknown as RmaContext)
                : context.rma ?? null;

        if (rmaContext) {
            this.logger.info('Restoring persisted RMA receiving session');
            this.rmaFacade.setActiveRmaTask(rmaContext);
            return of(null);
        }

        if (!context.purchaseOrderId || !context.requisitionId) {
            this.logger.info('No persisted receiving session to restore');
            return of(null);
        }

        return combineLatest([
            this.purchaseOrderFacade.getById$(context.purchaseOrderId),
            this.requisitionFacade.getById$(context.requisitionId),
        ]).pipe(
            tap(([purchaseOrder, requisition]) => {
                if (purchaseOrder && requisition) {
                    this.setActiveReceivingTask$(purchaseOrder!, requisition!);
                } else {
                    this.logger.warn(
                        `Session restore incomplete: PO=${context.purchaseOrderId} (found=${!!purchaseOrder}), ` +
                            `Requisition=${context.requisitionId} (found=${!!requisition})`
                    );
                }
            })
        );
    }

    /**
     * Validates a purchase order against warehouse, status, and requisition rules.
     * @returns A {@link ReceivingLookupResult} with the validation outcome.
     */
    private validatePurchaseOrder$(
        purchaseOrder: PurchaseOrderHeaderDto | null
    ): Observable<ReceivingLookupResult> {
        if (!purchaseOrder) {
            this.logger.warn('Purchase order lookup returned no results');
            return of({
                purchaseOrder: null,
                requisition: null,
                valid: false,
                error: ReceivingValidationError.NotFound,
            });
        }

        const requisitionId =
            purchaseOrder.purchaseOrderLines?.[0]?.requisitionId;

        if (!requisitionId) {
            this.logger.warn(
                `PO ${purchaseOrder.id} has no requisition linked to its first line`
            );
            return of({
                purchaseOrder,
                requisition: null,
                valid: false,
                error: ReceivingValidationError.InvalidPurchaseOrder,
            });
        }

        return this.requisitionFacade
            .getById$(requisitionId)
            .pipe(
                map(requisition =>
                    this.buildLookupResult(purchaseOrder, requisition)
                )
            );
    }

    /**
     * Produces a {@link ReceivingLookupResult} by checking the requisition and PO status.
     */
    private buildLookupResult(
        purchaseOrder: PurchaseOrderHeaderDto,
        requisition: RequisitionDto | null
    ): ReceivingLookupResult {
        if (!requisition) {
            this.logger.warn(
                `Requisition not found for PO ${purchaseOrder.id}`
            );
            return {
                purchaseOrder,
                requisition: null,
                valid: false,
                error: ReceivingValidationError.RequisitionNotFound,
            };
        }

        if (requisition.warehouse.id !== this.warehouseFacade.active!.id) {
            this.logger.warn(
                `PO ${purchaseOrder.id} belongs to warehouse ${requisition.warehouse.id}, ` +
                    `but active warehouse is ${this.warehouseFacade.active!.id}`
            );
            return {
                purchaseOrder,
                requisition,
                valid: false,
                error: ReceivingValidationError.WrongWarehouse,
            };
        }

        if (purchaseOrder.status === PurchaseOrderStatus.Completed) {
            this.logger.warn(
                `PO ${purchaseOrder.id} is already fully received`
            );
            return {
                purchaseOrder,
                requisition,
                valid: false,
                error: ReceivingValidationError.Received,
            };
        }

        return { purchaseOrder, requisition, valid: true };
    }

    /**
     * Loads exceptions for all receipt lines referenced by the given mappings.
     * @returns An observable emitting a flat array of non-null exceptions.
     */
    private loadExceptionsForMappings$(
        mappings: ReceiptLineMapping[]
    ): Observable<ExceptionDto[]> {
        const receiptLineIds = mappings
            .map(m => m.receiptLineId)
            .filter(Boolean);

        return combineLatest(
            receiptLineIds.map(id => this.getReceiptLineException$(id))
        ).pipe(map(results => results.filter(Boolean) as ExceptionDto[]));
    }

    /**
     * Combines selected PO lines with shipment keys and exceptions
     * to produce the final list of {@link ReceivingShipment}s.
     */
    private buildReceivingShipments$(
        keys: string[],
        exceptions: ExceptionDto[],
        mappings: ReceiptLineMapping[]
    ): Observable<ReceivingShipment[]> {
        return this.selectedLines$.pipe(
            map(lines =>
                this.mapLinesToShipments(lines, keys, exceptions, mappings)
            )
        );
    }

    /**
     * Pure mapping function: converts selected PO lines into {@link ReceivingShipment}s,
     * filtering to expected shipment keys and attaching any exception.
     */
    private mapLinesToShipments(
        lines: PoLineSummary[],
        keys: string[],
        exceptions: ExceptionDto[],
        mappings: ReceiptLineMapping[]
    ): ReceivingShipment[] {
        const shipments: ReceivingShipment[] = [];

        for (const poLine of lines) {
            if (poLine.asnShipments.length > 0) {
                for (const asn of poLine.asnShipments) {
                    const shipmentKey = ReceiptLineShipmentKey(
                        poLine.id,
                        asn.asnHeaderId
                    );
                    if (!keys.includes(shipmentKey)) continue;

                    const mapping = mappings.find(
                        m =>
                            m.poLineId === poLine.id &&
                            m.asnHeaderId === asn.asnHeaderId
                    );
                    const receipt = poLine.receipts.find(
                        r =>
                            r.receiptLineId === mapping?.receiptLineId &&
                            r.receiptHeaderId === mapping?.receiptHeaderId
                    );

                    if (!receipt) {
                        this.logger.error(
                            `No receipt found for ASN ${asn.asnHeaderId} on PO line ${poLine.id}`
                        );
                        continue;
                    }

                    shipments.push({
                        poLine,
                        shipmentKey,
                        asn,
                        receipt,
                        exception: this.findExceptionForReceipt(
                            exceptions,
                            receipt
                        ),
                    });
                }
            }

            const unidShipmentKey = ReceiptLineShipmentKey(poLine.id);
            if (!keys.includes(unidShipmentKey)) continue;

            const unidMapping = mappings.find(
                m => m.poLineId === poLine.id && !m.asnHeaderId
            );
            const unidReceipt = poLine.receipts.find(
                r =>
                    r.receiptLineId === unidMapping?.receiptLineId &&
                    r.receiptHeaderId === unidMapping?.receiptHeaderId
            );

            if (!unidReceipt) {
                this.logger.error(`No receipt found for PO line ${poLine.id}`);
                continue;
            }

            shipments.push({
                poLine,
                shipmentKey: unidShipmentKey,
                asn: null,
                receipt: unidReceipt,
                exception: this.findExceptionForReceipt(
                    exceptions,
                    unidReceipt
                ),
            });
        }

        return shipments;
    }

    /** Finds the first exception whose sourceEntityId matches the receipt line. */
    private findExceptionForReceipt(
        exceptions: ExceptionDto[],
        receipt: { receiptLineId: string }
    ): ExceptionDto | null {
        return (
            exceptions.find(e => e.sourceEntityId === receipt.receiptLineId) ??
            null
        );
    }

    /**
     * Searches for and returns the most recent exception linked to a receipt line.
     * Triggers a server search then reads from the local store.
     */
    private getReceiptLineException$(
        receiptLineId: string
    ): Observable<ExceptionDto | null> {
        return this.exceptionFacade
            .oneTimeSearch$({
                pageIndex: 1,
                pageSize: 1,
                filters: {
                    sourceEntity: WmsPlatformEntity.ReceiptLine,
                    sourceEntityId: receiptLineId,
                },
            })
            .pipe(
                switchMap(() =>
                    this.exceptionFacade.filterBySourceEntityId$(receiptLineId)
                ),
                map(exceptions => exceptions[0] ?? null),
                catchError(err => {
                    this.logger.error(
                        `Failed to load exception for receipt line ${receiptLineId}`,
                        err
                    );
                    return of(null);
                })
            );
    }
}
